import { supabase } from './supabase.js';

/**
 * ApplicationRequirementsService
 *
 * Generates application timeline items from college-specific requirements
 * stored in global_colleges.required_components and optional_components.
 *
 * Uses milestone_templates to provide detailed task information and automatically
 * calculate due dates relative to application deadlines.
 */

export class ApplicationRequirementsService {
  /**
   * Fetches milestone templates for application requirements
   * @returns {Promise<Map>} Map of template_key -> template
   */
  static async getApplicationTemplates() {
    const { data, error } = await supabase
      .from('milestone_templates')
      .select('*')
      .eq('category', 'application')
      .eq('is_active', true);

    if (error) {
      console.error('Error fetching application templates:', error);
      return new Map();
    }

    // Create a map keyed by template_key for fast lookup
    return new Map(data.map(template => [template.template_key, template]));
  }

  /**
   * Gets application requirements for a specific college
   * @param {string} collegeId - The global college ID
   * @returns {Promise<{required: Array, optional: Array}>}
   */
  static async getCollegeRequirements(collegeId) {
    const { data, error } = await supabase
      .from('global_colleges')
      .select('required_components, optional_components, name')
      .eq('id', collegeId)
      .single();

    if (error) {
      console.error('Error fetching college requirements:', error);
      return { required: [], optional: [], collegeName: null };
    }

    return {
      required: data?.required_components || [],
      optional: data?.optional_components || [],
      collegeName: data?.name
    };
  }

  /**
   * Calculates due date for a template based on deadline and offset
   * @param {Object} template - The milestone template
   * @param {string} applicationDeadline - The application deadline (ISO date string)
   * @returns {string|null} ISO date string for due date
   */
  static calculateDueDate(template, applicationDeadline) {
    if (!applicationDeadline) return null;

    const deadline = new Date(applicationDeadline);
    const offsetDays = template.offset_days || 0;

    // Negative offset = days before deadline, Positive offset = days after anchor
    deadline.setDate(deadline.getDate() + offsetDays);

    return deadline.toISOString().split('T')[0];
  }

  /**
   * Generates timeline items from application requirements
   * @param {string} userId - User ID
   * @param {string} collegeId - College ID
   * @param {string} collegeName - College name
   * @param {string} applicationDeadline - Application deadline (ISO date)
   * @param {Array} requirements - Array of requirement objects with template_keys
   * @param {Map} templates - Map of template_key -> template
   * @param {boolean} isRequired - Whether these are required (vs optional) components
   * @returns {Array} Timeline items ready for insertion
   */
  static generateTimelineItems(userId, collegeId, collegeName, applicationDeadline, requirements, templates, isRequired = true) {
    return requirements.map(req => {
      const template = templates.get(req.template_key);

      if (!template) {
        console.warn(`Template not found for key: ${req.template_key}`);
        return null;
      }

      // Build title with college name prefix
      let title = template.title;
      if (req.subject_area) {
        // For teacher recs, add subject area to title
        title = `${collegeName} - ${template.title} (${req.subject_area})`;
      } else {
        title = `${collegeName} - ${template.title}`;
      }

      // Calculate due date
      const dueDate = this.calculateDueDate(template, applicationDeadline);

      // Build description with any component-specific details
      let description = template.description;
      if (req.subjects && Array.isArray(req.subjects)) {
        description += ` (${req.subjects.join(', ')})`;
      }
      if (req.category) {
        description += ` - ${req.category}`;
      }

      return {
        user_id: userId,
        college_id: collegeId,
        title,
        description,
        category: 'application',
        phase: template.phase,
        priority: isRequired ? template.priority : 'low', // Optional components get low priority
        due_date: dueDate,
        scheduled_date: dueDate,
        status: 'not_started',
        source_type: 'milestone_template',
        source_id: `template_${template.id}`,
        tags: isRequired ? ['required', 'application'] : ['optional', 'application'],
        metadata: {
          template_id: template.id,
          template_key: template.template_key,
          component_type: req.type,
          is_required: isRequired,
          ...template.metadata,
          ...req
        }
      };
    }).filter(item => item !== null);
  }

  /**
   * Main function: Generate application timeline for a student/college
   * @param {string} userId - User ID
   * @param {string} collegeId - College ID from college_journey_colleges
   * @param {string} globalCollegeId - Global college ID
   * @param {string} applicationDeadline - Application deadline (ISO date)
   * @returns {Promise<{success: boolean, items: Array, error?: string}>}
   */
  static async generateApplicationTimeline(userId, collegeId, globalCollegeId, applicationDeadline) {
    try {
      // 1. Fetch templates
      const templates = await this.getApplicationTemplates();

      if (templates.size === 0) {
        return {
          success: false,
          items: [],
          error: 'No application templates found in database'
        };
      }

      // 2. Fetch college requirements
      const { required, optional, collegeName } = await this.getCollegeRequirements(globalCollegeId);

      if (required.length === 0 && optional.length === 0) {
        // No requirements stored yet - return empty but successful
        return {
          success: true,
          items: [],
          message: 'No application requirements found for this college. Scraper may not have run yet.'
        };
      }

      // 3. Generate timeline items for required components
      const requiredItems = this.generateTimelineItems(
        userId,
        collegeId,
        collegeName,
        applicationDeadline,
        required,
        templates,
        true
      );

      // 4. Generate timeline items for optional components
      const optionalItems = this.generateTimelineItems(
        userId,
        collegeId,
        collegeName,
        applicationDeadline,
        optional,
        templates,
        false
      );

      const allItems = [...requiredItems, ...optionalItems];

      // 5. Insert timeline items
      const { data, error } = await supabase
        .from('timeline_items')
        .insert(allItems)
        .select();

      if (error) {
        console.error('Error inserting timeline items:', error);
        return { success: false, items: [], error: error.message };
      }

      return {
        success: true,
        items: data || [],
        message: `Generated ${requiredItems.length} required and ${optionalItems.length} optional application tasks`
      };

    } catch (error) {
      console.error('Error generating application timeline:', error);
      return {
        success: false,
        items: [],
        error: error.message
      };
    }
  }

  /**
   * Get formatted application components list (for UI display, not timeline generation)
   * @param {string} globalCollegeId - Global college ID
   * @returns {Promise<Array>} Formatted components list
   */
  static async getFormattedApplicationComponents(globalCollegeId) {
    try {
      const templates = await this.getApplicationTemplates();
      const { required, optional } = await this.getCollegeRequirements(globalCollegeId);

      const formatComponent = (req, isRequired) => {
        const template = templates.get(req.template_key);
        if (!template) return null;

        let name = template.title;
        let details = template.description;

        // Add subject-specific details for teacher recs
        if (req.subject_area) {
          name = `${template.title} (${req.subject_area})`;
          if (req.subjects) {
            details = `${details}: ${req.subjects.join(', ')}`;
          }
        }

        // Add category for portfolios
        if (req.category) {
          name = `${template.title} (${req.category})`;
        }

        return {
          name,
          details,
          urgent: isRequired && template.priority === 'high',
          optional: !isRequired,
          template_key: req.template_key,
          component_type: req.type,
          estimated_hours: template.metadata?.estimated_hours
        };
      };

      const requiredComponents = required
        .map(req => formatComponent(req, true))
        .filter(comp => comp !== null);

      const optionalComponents = optional
        .map(req => formatComponent(req, false))
        .filter(comp => comp !== null);

      return [...requiredComponents, ...optionalComponents];

    } catch (error) {
      console.error('Error getting formatted components:', error);
      return [];
    }
  }
}
